<template>
  <div>
    <div v-if="loading" class="clearfix">
      <b-spinner class="float-right" label="Carregando..." />
    </div>
    <div v-else>
      <b-card>
        <template #header>
          <h4 class="mb-0">{{ form.isView ? 'Visualizar Negociação' : 'Nova Negociação (Novação)' }}</h4>
        </template>
        
        <validation-observer ref="form">
          <b-form @submit.prevent>
            <b-row>
              <!-- Seleção de Registro -->
              <b-col cols="12" md="6">
                <validation-provider #default="{ errors }" name="registro" rules="required">
                  <b-form-group label="Associado / Registro" label-for="v-registro">
                    <v-select
                      v-model="form.registro"
                      label="nome"
                      :filterable="false"
                      :options="comboRegistros"
                      :disabled="form.isView"
                      @search="onSearch"
                      @input="onRegistroSelected"
                    >
                      <template slot="no-options">
                        Pesquisa por nome, CPF ou matrícula
                      </template>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="6">
                <validation-provider #default="{ errors }" name="Data Lançamento" rules="required">
                  <b-form-group label="Data da Negociação" label-for="v-dataLancamento">
                    <flat-pickr
                      v-model="form.dataLancamento"
                      class="form-control"
                      :config="{ altInput: true, altFormat: 'd/m/Y', dateFormat: 'Y-m-d\\TH:i:ss\\Z', clickOpens: !form.isView }"
                      :disabled="form.isView"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Grade de Títulos para Seleção (Só mostra em Novo) -->
              <b-col v-if="form.registro && !form.isView" cols="12" class="mt-2">
                <b-card border-variant="primary" header="Títulos Disponíveis para Negociação" header-bg-variant="transparent">
                  <div v-if="loadingTitles" class="text-center p-3">
                    <b-spinner label="Buscando títulos..." />
                  </div>
                  <div v-else-if="titulosDisponiveis.length === 0" class="text-center p-3">
                    <b-alert variant="warning" show>Nenhum título em aberto encontrado para este associado.</b-alert>
                  </div>
                  <vue-good-table
                    v-else
                    :columns="titleColumns"
                    :rows="titulosDisponiveis"
                    :select-options="{ enabled: true, selectOnCheckboxOnly: true }"
                    @on-selected-rows-change="onSelectionChanged"
                  >
                    <template slot="table-row" slot-scope="props">
                      <span v-if="props.column.field === 'valorTotal'">
                        {{ formatCurrency(props.row.valorTotal) }}
                      </span>
                      <span v-else-if="props.column.field === 'dataVencimento'">
                        {{ formatDate(props.row.dataVencimento) }}
                      </span>
                    </template>
                  </vue-good-table>
                </b-card>
              </b-col>

              <!-- Resumo da Consolidação (Só mostra em Novo) -->
              <b-col v-if="selectedTitles.length > 0 && !form.isView" cols="12" class="mt-2">
                <b-alert variant="info" show class="d-flex justify-content-between align-items-center">
                  <span><strong>{{ selectedTitles.length }}</strong> títulos selecionados</span>
                  <span>Total Base: <strong>{{ formatCurrency(totalConsolidado) }}</strong></span>
                </b-alert>
              </b-col>

              <!-- Configurações do Novo Acordo (Mostra em Novo com seleção OU em Visualização) -->
              <template v-if="selectedTitles.length > 0 || form.isView">
                <b-col cols="12">
                   <hr>
                   <h5 class="mb-2">Configurações do Novo Acordo</h5>
                </b-col>

                <b-col cols="12" md="6">
                  <b-form-group label="Descrição da Negociação" label-for="v-descricao">
                    <b-form-input id="v-descricao" v-model="form.descricao" placeholder="Ex: Renegociação de débitos vencidos" :disabled="form.isView" />
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="3">
                  <validation-provider #default="{ errors }" name="valor" rules="required">
                    <b-form-group label="Valor Negociado (Principal)" label-for="v-valor">
                      <b-input-group prepend="R$">
                        <cleave v-model.number="form.valor" class="form-control" :raw="true" :options="moneyOptions" :disabled="form.isView" />
                      </b-input-group>
                      <small class="text-secondary">Pode ser ajustado com descontos/acréscimos</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col cols="12" md="3">
                  <b-form-group label="Quantidade de Parcelas" label-for="v-parcelas">
                    <b-form-input id="v-parcelas" v-model.number="form.qtoParcelas" type="number" :disabled="form.isView" />
                  </b-form-group>
                </b-col>

                <!-- Tipo de Lançamento -->
                <b-col cols="12" md="4">
                  <validation-provider #default="{ errors }" name="Tipo de Lançamento" rules="required">
                    <b-form-group label="Tipo de Lançamento" label-for="v-tipo">
                      <v-select v-model="form.tipo" :options="comboTipoPagamento" label="descricao" :reduce="t => t.id" :class="{ 'is-invalid': errors.length > 0 }" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col cols="12" md="4">
                  <b-form-group label="Taxa de Juros" label-for="v-taxa">
                    <v-select v-model="form.taxa" :options="comboTaxas" label="nome" :reduce="t => t.id" :disabled="form.isView" />
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="4">
                  <b-form-group label="Sistema de Amortização">
                    <v-select v-model="form.tipoAmortizacao" :options="amortizacaoOptions" :reduce="o => o.value" :disabled="form.isView" />
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="4">
                  <b-form-group label="Conta Financeira">
                    <v-select v-model="form.conta" :options="comboContas" label="descricao" :reduce="c => c.id" :disabled="form.isView" />
                  </b-form-group>
                </b-col>
              </template>

              <!-- VISUALIZAÇÃO: Composição Original -->
              <b-col v-if="form.isView && titulosOriginais.length > 0" cols="12" class="mt-4">
                <hr>
                <h5 class="mb-2 text-primary">
                  <feather-icon icon="FileTextIcon" class="mr-50" />
                  Composição Original da Dívida (Títulos Liquidados)
                </h5>
                <vue-good-table
                  :columns="originalCompositionColumns"
                  :rows="titulosOriginais"
                  style-class="vgt-table striped"
                >
                  <template slot="table-row" slot-scope="props">
                    <span v-if="['valorTotal', 'juro', 'multa'].includes(props.column.field)">
                      {{ formatCurrency(props.row[props.column.field]) }}
                    </span>
                    <span v-else-if="props.column.field === 'dataVencimento'">
                      {{ formatDate(props.row.dataVencimento) }}
                    </span>
                  </template>
                </vue-good-table>
                <div class="text-right mt-1">
                   <p class="h6">Total da Composição: <strong>{{ formatCurrency(totalComposicaoOriginal) }}</strong></p>
                </div>
              </b-col>

              <!-- VISUALIZAÇÃO: Novas Parcelas -->
              <b-col v-if="form.isView && itensGerados.length > 0" cols="12" class="mt-4">
                <hr>
                <h5 class="mb-2 text-success">
                  <feather-icon icon="HashIcon" class="mr-50" />
                  Novas Parcelas Geradas (Cronograma de Pagamento)
                </h5>
                <vue-good-table
                  :columns="generatedInstallmentsColumns"
                  :rows="itensGerados"
                  style-class="vgt-table striped"
                >
                  <template slot="table-row" slot-scope="props">
                    <span v-if="['prestacao', 'juros', 'amortizacao'].includes(props.column.field)">
                      {{ formatCurrency(props.row[props.column.field]) }}
                    </span>
                    <span v-else-if="props.column.field === 'dataVencimento'">
                      {{ formatDate(props.row.dataVencimento) }}
                    </span>
                  </template>
                </vue-good-table>
              </b-col>

                <b-col v-if="!form.isView" cols="12" class="mt-3 text-right">
                  <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" type="button" variant="outline-secondary" class="mr-1" @click="cancelar">
                    Cancelar
                  </b-button>
                  <b-button v-if="selectedTitles.length > 0" v-ripple.400="'rgba(255, 255, 255, 0.15)'" type="button" variant="primary" @click="submit">
                    Gerar Negociação
                  </b-button>
                </b-col>
                <b-col v-else cols="12" class="mt-3 text-right">
                  <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" type="button" variant="primary" @click="cancelar">
                    Fechar
                  </b-button>
                </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BFormGroup, BFormInput, BForm, BSpinner, BAlert
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import Cleave from 'vue-cleave-component'
import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ripple from 'vue-ripple-directive'
import _ from 'lodash'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BForm,
    BSpinner,
    BAlert,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    flatPickr,
    Cleave,
    VueGoodTable,
  },
  directives: { ripple },
  props: {
    editId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      loadingTitles: false,
      comboRegistros: [],
      comboTaxas: [],
      comboContas: [],
      titulosDisponiveis: [],
      selectedTitles: [],
      titulosOriginais: [],
      itensGerados: [],
      comboTipoPagamento: [],
      form: {
        registro: null,
        dataLancamento: new Date().toISOString(),
        descricao: '',
        valor: 0,
        qtoParcelas: 1,
        taxa: null,
        tipo: null,
        tipoAmortizacao: 'PRICE',
        conta: null,
        formaPagamento: 'RENEGOCIAÇÃO'
      },
      titleColumns: [
        { label: 'Vencimento', field: 'dataVencimento' },
        { label: 'Descrição', field: 'descricao' },
        { label: 'Valor Total', field: 'valorTotal' },
        { label: 'Situação', field: 'situacao' },
      ],
      originalCompositionColumns: [
        { label: 'Descrição', field: 'descricao' },
        { label: 'Vencimento', field: 'dataVencimento' },
        { label: 'Juros', field: 'juro' },
        { label: 'Multa', field: 'multa' },
        { label: 'Valor Total', field: 'valorTotal' },
      ],
      generatedInstallmentsColumns: [
        { label: 'Nº', field: 'numeroParcela' },
        { label: 'Vencimento', field: 'dataVencimento' },
        { label: 'Juros', field: 'juros' },
        { label: 'Amortização', field: 'amortizacao' },
        { label: 'Valor Parcela', field: 'prestacao' },
      ],
      amortizacaoOptions: [
        { label: 'Tabela PRICE (Fixas)', value: 'PRICE' },
        { label: 'SAC (Decrescentes)', value: 'SAC' },
      ],
      moneyOptions: {
        numeral: true,
        numeralDecimalMark: ',',
        delimiter: '.',
      }
    }
  },
  computed: {
    totalConsolidado() {
      return this.selectedTitles.reduce((acc, curr) => acc + (curr.valorTotal || 0), 0)
    },
    totalComposicaoOriginal() {
      return this.titulosOriginais.reduce((acc, curr) => acc + (curr.valorTotal || 0), 0)
    }
  },
  async mounted() {
    this.loading = true
    await this.loadCombos()
    
    // Se recebeu ID via prop ou rota, carrega os dados para visualização
    const id = this.editId || this.$route.params.id
    if (id) {
      await this.loadNegociacao(id)
    }
    
    this.loading = false
  },
  methods: {
    async loadCombos() {
      // Carrega Taxas e Contas bases usando o módulo de combos
      try {
        const resTaxas = await this.$store.dispatch('combosModule/loadComboTaxas')
        this.comboTaxas = resTaxas.data
        const resContas = await this.$store.dispatch('combosModule/loadComboContas')
        this.comboContas = resContas.data
        const resTipos = await this.$store.dispatch('combosModule/loadComboTipoPagamento')
        this.comboTipoPagamento = resTipos.data
      } catch (e) {
        console.error('Erro ao carregar combos', e)
      }
    },
    async loadNegociacao(id) {
      try {
        const data = await this.$store.dispatch('parcelamentoFinananceiroModule/getParcelamentoPorId', id)
        this.form = {
          id: data.id,
          registro: data.registro,
          dataLancamento: data.dataLancamento,
          descricao: data.descricao,
          valor: data.valor,
          qtoParcelas: data.qtoParcelas,
          taxa: data.taxa?.id,
          tipo: data.tipo?.id,
          tipoAmortizacao: data.tipoAmortizacao,
          conta: data.conta?.id,
          formaPagamento: data.formaPagamento,
          isView: true // Flag para desativar edição
        }
        this.titulosOriginais = data.titulosOriginais || []
        // Ordena itens pela parcela
        this.itensGerados = (data.itens || []).sort((a, b) => a.numeroParcela - b.numeroParcela)
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Erro ao carregar dados da negociação', icon: 'AlertTriangleIcon', variant: 'danger' }
        })
      }
    },
    cancelar() {
      this.$emit('cancelar')
      // Se não for consumido pelo pai (v-if), tenta voltar a rota
      if (this.$route.name === 'editnegociacao') {
        this.$router.go(-1)
      }
    },
    onSearch(search, loading) {
      if (search.length < 3) return
      loading(true)
      this.debouncedSearch(search, loading)
    },
    debouncedSearch: _.debounce(function (search, loading) {
      this.$store.dispatch('pagamentoRecebimentoFinancialModule/searchRegistros', search)
        .then(res => {
          // Garante que ainda estamos buscando pelo mesmo termo
          this.comboRegistros = res.data.content
          loading(false)
        })
        .catch(() => {
          loading(false)
        })
    }, 500),
    async onRegistroSelected(registro) {
      if (!registro) {
        this.titulosDisponiveis = []
        return
      }
      this.loadingTitles = true
      try {
        // Busca títulos do associado sem filtro de situação no servidor para evitar limitações do DTO
        const filter = {
          registroId: registro.id
        }
        // Usando a ação que mapeamos anteriormente
        const res = await this.$store.dispatch('pagamentoRecebimentoFinancialModule/filterAllTransacoesSemMutation', {
          filter,
          pageConfig: { page: 1, size: 100, sort: 'dataVencimento', order: 'asc' }
        })
        // Filtra no frontend para mostrar apenas débitos que já passaram do prazo ou foram pagos parcialmente
        // Criterio: VENCIDO (1) ou PAGO_A_MENOR (4)
        const situacoesAtrasadas = [1, 4, 'VENCIDO', 'PAGO_A_MENOR']
        const hoje = new Date().toISOString().split('T')[0]
        
        this.titulosDisponiveis = res.data.content.filter(t => {
          const isVencidoExplicit = situacoesAtrasadas.includes(t.situacao) || 
                                   (t.situacao && situacoesAtrasadas.includes(t.situacao.id))
          
          // Caso o sistema ainda marque como ABERTO (0), mas a data seja menor que hoje, também consideramos vencido
          const isAbertoVencido = (t.situacao === 0 || (t.situacao && t.situacao.id === 0)) && 
                                  t.dataVencimento < hoje
                                  
          return isVencidoExplicit || isAbertoVencido
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Erro ao buscar títulos', icon: 'AlertTriangleIcon', variant: 'danger' }
        })
      } finally {
        this.loadingTitles = false
      }
    },
    onSelectionChanged(params) {
      this.selectedTitles = params.selectedRows
      this.form.valor = this.totalConsolidado
      if (this.selectedTitles.length > 0 && !this.form.descricao) {
        this.form.descricao = `Renegociação de ${this.selectedTitles.length} títulos`
      }
    },
    formatCurrency(val) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val)
    },
    formatDate(date) {
      if (!date) return '-'
      if (typeof date === 'string' && date.length === 10) {
        // Formato YYYY-MM-DD
        const [year, month, day] = date.split('-')
        return `${day}/${month}/${year}`
      }
      return new Date(date).toLocaleDateString('pt-BR')
    },
    async submit() {
      const valid = await this.$refs.form.validate()
      if (!valid) return

      if (!this.form.registro || !this.form.conta || !this.form.taxa || !this.form.tipo) {
        this.$toast({
          component: ToastificationContent,
          props: { 
            title: 'Campos incompletos', 
            text: 'Por favor, selecione o Associado, a Conta, a Taxa e o Tipo de Lançamento.',
            icon: 'AlertTriangleIcon', 
            variant: 'warning' 
          }
        })
        return
      }

      this.loading = true
      try {
        const payload = {
          pagamentoIds: this.selectedTitles.map(t => t.id),
          novoAcordo: {
            ...this.form,
            registro: { id: this.form.registro.id },
            taxa: { id: this.form.taxa },
            conta: { id: this.form.conta },
            tipo: { id: this.form.tipo }, 
            confirmado: true
          }
        }

        // Endpoint que criamos no backend
        await this.$http.post('/financeiro/parcelamentos/renegociar', payload)

        // Notifica sucesso para o pai fechar o formulário
        this.$emit('finalizado')

        this.$toast({
          component: ToastificationContent,
          props: { title: 'Negociação criada com sucesso!', icon: 'CheckIcon', variant: 'success' }
        })

        // Evita erro de navegação duplicada se já estivermos na rota principal
        if (this.$route.name !== 'negociacao') {
          this.$router.push({ name: 'negociacao' }).catch(() => {})
        }
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Erro ao gerar negociação', icon: 'AlertTriangleIcon', variant: 'danger' }
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
